import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core'
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'

@Component({
  selector: 'basin-numeric-range',
  templateUrl: './numeric-range.component.html',
  styleUrls: [],
  providers: [
    // This registers the component as a NG_VALUE_ACCESSOR provider for form control.
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericRangeComponent),
      multi: true,
    },
  ],
})
export class NumericRangeComponent implements ControlValueAccessor {
  @Input() styleClass: string
  @Input() placeholder: string = ''
  @Input() defaultValue: string = ''
  @Input() required: boolean
  @Input() disabled: boolean
  @Input() readonly: boolean
  @Input() validRanges: string
  @Input() validationMessages: { [key: string]: string } = {}
  @Input() outputFormat: string = 'string'
  displayValue: string

  @Input() formControl: FormControl
  @Input() ngModel: any

  @Output() ngModelChange: EventEmitter<string> = new EventEmitter<string>()
  @Output() blur: EventEmitter<string> = new EventEmitter<string>()
  @Output() input: EventEmitter<string> = new EventEmitter<string>()
  @Output() keydown: EventEmitter<string> = new EventEmitter<string>()
  @Output() clear: EventEmitter<string> = new EventEmitter<string>()
  onModelChange: Function = () => {}
  onModelTouched: Function = () => {}

  constructor(private cdRef: ChangeDetectorRef) {}

  writeValue(value: any): void {
    if (value !== undefined) {
      this.ngModel = value
      this.displayValue = this.ngModel
      this.cdRef.markForCheck()
    }
  }

  handleValueChange(value: string) {
    switch (this.outputFormat) {
      case 'array':
        this.ngModel = value.split(',').map((x) => x.trim())
        break
      case 'expandedArray':
        let output: number[] = []
        this.ngModel = value.split(',').map((range) => {
          const processedRange = this.processRange(range)
          output = output.concat(processedRange)
        })
        output = [...new Set(output)] //remove duplicate values
        this.ngModel = output
        break
      case 'string':
      default:
        this.ngModel = value
        break
    }

    if (value === '') this.ngModel = this.defaultValue
    this.ngModelChange.emit(this.ngModel)
    this.onModelChange(this.ngModel)
    this.onModelTouched()
  }

  onFocus() {}

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Home' || event.key === 'End') {
      event.stopPropagation()
    }
  }
  onClear() {}

  onBlur() {
    this.onModelTouched()
    this.blur.emit(this.ngModel)
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    this.cdRef.markForCheck()
  }

  processRange(range: string) {
    const numbers: number[] = []
    if (isNaN(Number(range))) {
      // if value is not a number
      if (range.includes('-')) {
        // if value contains a hyphen
        const [start, end] = range.split('-').map((x) => parseInt(x))
        if (isNaN(start) || isNaN(end) || start > end) numbers.push(NaN)
        else for (let i = start; i <= end; i++) numbers.push(i)
      } else {
        if (this.isNumeric(range)) numbers.push(parseInt(range))
        else numbers.push(NaN)
      }
    } else numbers.push(parseInt(range))
    return numbers
  }

  isNumeric(input: string): boolean {
    const regex = /^\d+$/
    return regex.test(input)
  }
}
